<template>
  <v-card>
    <v-card-title>
      <v-container fluid class="pa-0">
        <v-row>
          <v-col>
            Negative keywords and approved booking details
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-switch v-model="showFilterKWA" label="Show keyword filters" @change="showFilterKWAChanged"></v-switch>
          </v-col>
          <v-col>
            <v-switch v-model="showFilterVP" label="Show verticals &amp; providers filters" @change="showFilterVPChanged"></v-switch>
          </v-col>
          <v-col>
            <v-switch v-model="showFilterTer" label="Show territories filters" @change="showFilterTerChanged"></v-switch>
          </v-col>
          <v-col>
            <v-btn class="mt-2" color="primary" @click="search">Search</v-btn>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="4">
            <v-autocomplete v-model="deleteByProviderID" :items="activeProviders" item-text="name" item-value="id" label="Delete all bookings by provider"
              clearable prepend-icon="delete_sweep" :disabled="deletingByProvider">
            </v-autocomplete>
          </v-col>
          <v-col cols="2" class="d-flex align-center">
            <v-btn small color="error" :disabled="!deleteByProviderID" :loading="deletingByProvider" @click="confirmDeleteByProvider()">Delete by provider</v-btn>
          </v-col>
        </v-row>

        <v-row v-show="showFilterKWA">
          <v-col>
            <v-text-field v-model="keywordText" label="Exact keyword text" clearable prepend-icon="text_snippet">
            </v-text-field>
          </v-col>

          <v-col>
            <v-text-field v-model="keywordSearch" label="Part of keyword" clearable prepend-icon="text_snippet">
            </v-text-field>
          </v-col>

          <v-col>
            <v-autocomplete v-model="selectedMatchTypes" :items="matchTypes" multiple label="Match types" clearable prepend-icon="format_size">
            </v-autocomplete>
          </v-col>

          <v-col>
            <v-autocomplete v-model="selectedKeywordTypes" :items="keywordTypes" multiple label="Keyword types" clearable item-text="name" item-value="name"
              prepend-icon="remove_circle_outline">
              <template v-slot:item="data">
                <v-list-item-action>
                  <v-icon v-if="data.attrs.inputValue">check_box</v-icon>
                  <v-icon v-else>check_box_outline_blank</v-icon>
                </v-list-item-action>
                <v-list-item-icon>
                  <v-icon>{{data.item.icon}}</v-icon>
                </v-list-item-icon>
                <v-list-item-content class="ml-4">
                  <v-list-item-title v-text="data.item.name"></v-list-item-title>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>

        <v-row v-show="showFilterVP">
          <v-col>
            <v-autocomplete v-model="selectedVerticals" :items="activeVerticals" item-text="name" item-value="name" multiple label="Verticals" 
              clearable prepend-icon="construction">
            </v-autocomplete>
          </v-col>

          <v-col>
            <v-autocomplete v-model="selectedProviders" :items="activeProviders" item-text="name" item-value="name" multiple label="Providers" 
              clearable prepend-icon="business">
            </v-autocomplete>
          </v-col>

          <v-col>
          </v-col>

          <v-col>
          </v-col>
        </v-row>

        <v-row v-show="showFilterTer">
          <v-col>
            <v-autocomplete v-model="selectedRegions" :items="regions" item-text="name" item-value="name" multiple label="Regions" 
              clearable prepend-icon="dashboard">
            </v-autocomplete>
          </v-col>

          <v-col>
            <v-autocomplete v-model="selectedCountries" :items="activeCountries" item-text="name" item-value="name" multiple label="Countries" 
              clearable prepend-icon="public">
            </v-autocomplete>
          </v-col>

          <v-col>
          </v-col>

          <v-col>
          </v-col>
        </v-row>
      </v-container>

    </v-card-title>

    <v-toolbar v-if="selectedItems.length > 0" dense flat color="grey lighten-4" class="mx-4 mb-2">
      <span class="body-2">{{ selectedItems.length }} selected</span>
      <v-spacer></v-spacer>
      <v-btn small color="primary" class="mr-2" @click="showChangeRegionDialog = true">Replace territories</v-btn>
      <v-btn small color="error" :loading="bulkDeleting" @click="showBulkDeleteDialog = true">Delete selected ({{ selectedItems.length }})</v-btn>
    </v-toolbar>

    <v-data-table
      v-model="selectedItems"
      :headers="headers"
      :items="items"
      :loading="loading"
      :options.sync="options"
      :footer-props="{
        'items-per-page-options': datatables_rowsPerPageItems,
      }"
      :server-items-length="totalItems"
      show-select
      item-key="id"
      class="elevation-1"
    >
      <template v-slot:loading>
        Loading items..
      </template>

      <template v-slot:[`header.keyword_type`]="{ header }">
        <v-tooltip>
          <template v-slot:activator="{ on }">
            <v-icon small v-on="on">help</v-icon>
          </template>
          <span>
            <v-icon color="white">copyright</v-icon> is Brand, <v-icon color="white">sports_kabaddi</v-icon> is Competitor, <v-icon color="white">speed</v-icon> is Performance
          </span>
        </v-tooltip>
        {{ header.text }}
      </template>

      <template v-slot:[`item.keyword_type`]="{ item }">
        <v-icon>{{ nkw_getTypeIcon(item.keyword_type) }}</v-icon>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-btn icon :to="{ name: 'Negative keyword', params: { nkwID: item.id }}">
          <v-icon small>edit</v-icon>
        </v-btn>
      </template>

      <template v-slot:[`footer.page-text`]="props">
        {{ datatables_footerPageText(totalItemsIsEstimate, estimatedItems, props) }}
      </template>

    </v-data-table>

    <v-dialog v-model="showBulkDeleteDialog" max-width="500px">
      <v-card>
        <v-card-title>Confirm bulk delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete {{ selectedItems.length }} selected keyword(s)?
          <br /><br />
          <strong>Warning:</strong> All associated bookings and comments will also be deleted.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showBulkDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" text :loading="bulkDeleting" @click="bulkDeleteSelected()">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDeleteByProviderDialog" max-width="500px">
      <v-card>
        <v-card-title>Confirm delete by provider</v-card-title>
        <v-card-text>
          Are you sure you want to delete <strong>all bookings</strong> for provider "{{ deleteByProviderName }}"?
          <br /><br />
          This will remove all region and country bookings associated with this provider. This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDeleteByProviderDialog = false">Cancel</v-btn>
          <v-btn color="error" text :loading="deletingByProvider" @click="deleteByProvider()">Delete all</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <BulkChangeTerritoryDialog
      :visible="showChangeRegionDialog"
      :selected-count="selectedItems.length"
      :loading="changingRegion"
      @confirm="bulkChangeTerritory"
      @cancel="showChangeRegionDialog = false"
    />
  </v-card>
</template>

<script>
import dataTablesMixin from '@/mixins/datatables'
import nkwMixin from '@/mixins/negativeKeywords'
import BulkChangeTerritoryDialog from '@/components/negativeKeywords/BulkChangeTerritoryDialog'

export default {
  name: 'NegativeKeywordsTable',

  data () {
    return {
      headers: [
        { text: 'Keyword', value: 'notated_keyword' },
        { text: 'Type', value: 'keyword_type' },
        { text: 'Verticals', value: 'vertical_short_names_csv' },
        { text: 'Providers', value: 'providers_csv' },
        { text: 'Territories', value: 'territories_csv' },
        { text: '# comments', value: 'comments_count' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      items: [],
      loading: false,
      totalItems: 0,
      estimatedItems: 0,
      totalItemsIsEstimate: false,
      options: {},
      showFilterKWA: false,
      showFilterVP: false,
      showFilterTer: false,
      keywordText: '',
      keywordSearch: '',
      selectedMatchTypes: [],
      selectedKeywordTypes: [],
      selectedProviders: [],
      selectedVerticals: [],
      selectedRegions: [],
      selectedCountries: [],
      lsOptions: 'negativeKeywords_options',
      lsFilters: 'negativeKeywords_filters',
      dataEndpoint: '/a/nkw/keywords',
      selectedItems: [],
      showBulkDeleteDialog: false,
      bulkDeleting: false,
      deleteByProviderID: null,
      showDeleteByProviderDialog: false,
      deletingByProvider: false,
      showChangeRegionDialog: false,
      changingRegion: false,
    }
  },

  mixins: [dataTablesMixin, nkwMixin],

  components: {
    BulkChangeTerritoryDialog,
  },

  computed: {
    activeCountries () {
      return this.$store.state.common.activeCountries
    },
    activeProviders () {
      return this.$store.state.core.allProviders
    },
    activeVerticals () {
      return this.$store.state.core.activeVerticals
    },
    deleteByProviderName () {
      if (!this.deleteByProviderID) return ''
      var provider = this.activeProviders.find(p => p.id === this.deleteByProviderID)
      return provider ? provider.name : ''
    },
    keywordTypes () {
      return this.$store.state.nkw.keywordTypes
    },
    matchTypes () {
      return this.$store.state.core.matchTypes
    },
    regions () {
      return this.$store.state.common.regions
    },
  },

  watch: {
    options: {
      handler () {
        this.fetchData()
      },
      deep: true,
    },
  },

  methods: {
    fetchData: function () {
      this.loading = true
      var myURL = this.dataEndpoint + '?xfields='

      // get fields from headers
      myURL = this.datatables_addHeaderFieldsToURI(myURL, this.headers, ['actions'], ['id'])

      // get paging and sorting from options via data table component and save to localStorage
      myURL = this.datatables_processURIOptions(myURL, this.options)
      localStorage.setItem(this.lsOptions, JSON.stringify(this.options))

      // filter keyword text
      if (this.keywordText) {
        myURL += '&keyword=' + this.keywordText
      }

      // filter keyword search
      if (this.keywordSearch) {
        myURL += '&keyword=~' + this.keywordSearch + '~'
      }

      // filter match types
      if (this.selectedMatchTypes.length > 0) {
        myURL += '&match_type=' + this.selectedMatchTypes.join('|')
      }

      // filter NKW types
      if (this.selectedKeywordTypes.length > 0) {
        myURL += '&keyword_type=' + this.selectedKeywordTypes.join('|')
      }

      // filter verticals
      if (this.selectedVerticals.length > 0) {
        myURL += '&verticals_csv=~[' + this.selectedVerticals.join('|') + ']~'
      }

      // filter providers
      if (this.selectedProviders.length > 0) {
        myURL += '&providers_csv=~[' + this.selectedProviders.join('|') + ']~'
      }

      // filter regions
      if (this.selectedRegions.length > 0) {
        myURL += '&regions_csv=~[' + this.selectedRegions.join('|') + ']~'
      }

      // filter countries
      if (this.selectedCountries.length > 0) {
        myURL += '&countries_csv=~[' + this.selectedCountries.join('|') + ']~'
      }

      this.$http.get(myURL).then(resp => {
        if (resp.data.data) {
          this.items = resp.data.data
          this.totalItemsIsEstimate = resp.headers['x-total-count-estimated'] === 'true' ? true : false
          var itemCounts = this.datatables_getItemCounts(this.totalItemsIsEstimate, Number(resp.headers['x-total-count']))
          this.totalItems = itemCounts.totalItems
          this.estimatedItems = itemCounts.estimatedItems
        } else {
          this.items = []
        }
      })
      .catch(errResp => {
        this.$store.commit('system/setAPIError', { method: 'GET', url: myURL, description: errResp.response.data.err_description, ext_msg: errResp.response.data.external_message })
      })
      .finally(() => this.loading = false)
    },

    search: function () {
      let filterObj = {
        'showFilterKWA': this.showFilterKWA,
        'showFilterVP': this.showFilterVP,
        'showFilterTer': this.showFilterTer,
        'keywordText': this.keywordText,
        'keywordSearch': this.keywordSearch,
        'selectedMatchTypes': this.selectedMatchTypes,
        'selectedKeywordTypes': this.selectedKeywordTypes,
        'selectedVerticals': this.selectedVerticals,
        'selectedProviders': this.selectedProviders,
        'selectedRegions': this.selectedRegions,
        'selectedCountries': this.selectedCountries,
      }
      localStorage.setItem(this.lsFilters, JSON.stringify(filterObj))

      this.options.page = 1;
      this.fetchData()
    },

    bulkDeleteSelected: function () {
      if (this.bulkDeleting) return

      this.bulkDeleting = true
      var url = '/a/nkw/delete-keywords'
      var keywordIDs = this.selectedItems.map(item => item.id)
      var body = JSON.stringify({ keywordIDs: keywordIDs })

      this.$http.put(url, body).then(resp => {
        this.selectedItems = []
        this.fetchData()
      })
      .catch(errResp => {
        this.$store.commit('system/setAPIError', { method: 'PUT', url: url, description: errResp.response.data.err_description, ext_msg: errResp.response.data.external_message })
      })
      .finally(() => {
        this.bulkDeleting = false
        this.showBulkDeleteDialog = false
      })
    },

    confirmDeleteByProvider: function () {
      if (!this.deleteByProviderID) return
      this.showDeleteByProviderDialog = true
    },

    bulkChangeTerritory: function (territory) {
      if (this.changingRegion) return

      this.changingRegion = true

      // fetch all booking IDs for the selected keywords
      var keywordIDs = this.selectedItems.map(item => item.id)
      var bookingsURL = '/a/nkw/bookings?xfields=id&xper_page=5000&keywords_fk=' + keywordIDs.join('|')

      this.$http.get(bookingsURL).then(resp => {
        var bookings = resp.data.data
        if (!bookings || bookings.length === 0) {
          alert('No bookings found for the selected keywords')
          this.changingRegion = false
          this.showChangeRegionDialog = false
          return
        }

        var bookingIDs = bookings.map(b => b.id)
        var url = '/a/nkw/replace-territories'
        var body = JSON.stringify({
          bookingIDs: bookingIDs,
          newRegionIDs: territory.newRegionIDs,
          newCountryIDs: territory.newCountryIDs,
        })

        this.$http.put(url, body).then(resp => {
          var data = resp.data.data
          var msg = data.createdCount + ' booking(s) created'
          if (data.deletedCount > 0) {
            msg += ', ' + data.deletedCount + ' removed (replaced)'
          }
          if (data.skippedCount > 0) {
            msg += ', ' + data.skippedCount + ' skipped (already exist)'
          }
          alert(msg)
          this.selectedItems = []
          this.fetchData()
        })
        .catch(errResp => {
          this.$store.commit('system/setAPIError', { method: 'PUT', url: url, description: errResp.response.data.err_description, ext_msg: errResp.response.data.external_message })
        })
        .finally(() => {
          this.changingRegion = false
          this.showChangeRegionDialog = false
        })
      })
      .catch(errResp => {
        this.$store.commit('system/setAPIError', { method: 'GET', url: bookingsURL, description: errResp.response.data.err_description, ext_msg: errResp.response.data.external_message })
        this.changingRegion = false
        this.showChangeRegionDialog = false
      })
    },

    deleteByProvider: function () {
      if (this.deletingByProvider) return

      this.deletingByProvider = true
      var url = '/a/nkw/delete-bookings-by-provider'
      var body = JSON.stringify({ providerID: this.deleteByProviderID })

      this.$http.put(url, body).then(resp => {
        var data = resp.data.data
        alert(data.deletedCount + ' booking(s) deleted for provider "' + this.deleteByProviderName + '"')
        this.deleteByProviderID = null
        this.fetchData()
      })
      .catch(errResp => {
        this.$store.commit('system/setAPIError', { method: 'PUT', url: url, description: errResp.response.data.err_description, ext_msg: errResp.response.data.external_message })
      })
      .finally(() => {
        this.deletingByProvider = false
        this.showDeleteByProviderDialog = false
      })
    },

    showFilterKWAChanged () {
      if (!this.showFilterKWA) {
        this.keywordSearch = ''
        this.selectedMatchTypes = []
        this.selectedKeywordTypes = []
        this.search()
      }
    },

    showFilterVPChanged () {
      if (!this.showFilterVP) {
        this.selectedVerticals = []
        this.selectedProviders = []
        this.search()
      }
    },

    showFilterTerChanged () {
      if (!this.showFilterVP) {
        this.selectedRegions = []
        this.selectedCountries = []
        this.search()
      }
    }
  },

  created: function () {
    this.timer = setInterval(this.fetchData, this.datatables_refreshIntervalMs)

    // if present, use local sorting/paging options
    if (localStorage.getItem(this.lsOptions)) {
      this.options = JSON.parse(localStorage.getItem(this.lsOptions))
      // setting multisort here: in v-data-table didn't work
      this.options['multiSort'] = true
    } else {
      this.options = {'multiSort': true}
    }

    // if present, use local filters
    if (localStorage.getItem(this.lsFilters)) {
      let filters = JSON.parse(localStorage.getItem(this.lsFilters))
      if (filters['showFilterKWA']) { this.showFilterKWA = filters['showFilterKWA'] }
      if (filters['showFilterVP']) { this.showFilterVP = filters['showFilterVP'] }
      if (filters['showFilterTer']) { this.showFilterTer = filters['showFilterTer'] }

      if (filters['keywordText']) { this.keywordText = filters['keywordText'] }
      if (filters['keywordSearch']) { this.keywordSearch = filters['keywordSearch'] }
      if (filters['selectedMatchTypes']) { this.selectedMatchTypes = filters['selectedMatchTypes'] }
      if (filters['selectedKeywordTypes']) { this.selectedKeywordTypes = filters['selectedKeywordTypes'] }

      if (filters['selectedVerticals']) { this.selectedVerticals = filters['selectedVerticals'] }
      if (filters['selectedProviders']) { this.selectedProviders = filters['selectedProviders'] }

      if (filters['selectedRegions']) { this.selectedRegions = filters['selectedRegions'] }
      if (filters['selectedCountries']) { this.selectedCountries = filters['selectedCountries'] }
    }
  },

  beforeDestroy () {
    clearInterval(this.timer)
  },
}
</script>
